import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface MenuItemRating {
  averageRating: number;
  totalRatings: number;
  recentReviews: Array<{
    id: string;
    rating: number;
    comment: string | null;
    customer_name: string | null;
    created_at: string;
  }>;
}

export const useMenuItemRatings = (menuItemId: string) => {
  const [ratings, setRatings] = useState<MenuItemRating>({
    averageRating: 0,
    totalRatings: 0,
    recentReviews: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRatings = async () => {
    if (!menuItemId) {
      console.log('❌ No menuItemId provided');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // Test if we can access the feedback table at all
      const { data, error } = await supabase
        .from('feedback')
        .select('*')
        .eq('menu_item_id', menuItemId)
        .eq('feedback_type', 'rating')
        .not('rating', 'is', null)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Supabase error:', error);
        console.error('❌ Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        
        // Check if this is a RLS policy error
        if (error.message?.includes('RLS') || error.message?.includes('policy') || error.code === 'PGRST301') {
          console.error('🚨 RLS POLICY ERROR: This is likely the issue!');
          console.error('🔧 Fix: Add policy to allow anonymous users to SELECT from feedback table');
          setError('Database access restricted. Please check RLS policies.');
        } else {
          console.error('🌐 Network or other error');
          setError(error.message);
        }
        throw error;
      }

      const feedbackData = data || [];
      const totalRatings = feedbackData.length;
      const averageRating = totalRatings > 0 
        ? feedbackData.reduce((sum, r) => sum + (r.rating || 0), 0) / totalRatings 
        : 0;

      const recentReviews = feedbackData.slice(0, 5).map(r => ({
        id: r.id,
        rating: r.rating || 0,
        comment: r.comment,
        customer_name: r.customer_name,
        created_at: r.created_at || ''
      }));

      setRatings({
        averageRating,
        totalRatings,
        recentReviews
      });
    } catch (error) {
      console.error('💥 Critical error fetching menu item ratings:', error);
      console.error('🔗 Network details:', {
        online: navigator.onLine,
        host: window.location.host,
        protocol: window.location.protocol,
        userAgent: navigator.userAgent.slice(0, 100) + '...'
      });
      setError(error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRatings();
  }, [menuItemId]);

  return { ratings, loading, error, refetch: fetchRatings };
};
